import { userRepo } from '@repositories';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import {
    remult,
    type ClassType,
    type EntityFilter,
    type EntityOrderBy,
    type MembersToInclude,
} from 'remult';
import {
    usePagination,
    useSessionStorage,
    type PaginationConfig,
    type PaginationMethod,
} from '../hooks';
import { useDebounce } from '../utils/hilma-tools';

export type HyperTableFindResult<Entity> = ReturnType<
    typeof useHyperTableFind<Entity>
>;

export interface RemultReactTableConfig<EntityType> {
    sessionStorageKey: string;
    include?: MembersToInclude<EntityType>;
    initialOrderBy?: EntityOrderBy<EntityType>;
    /** Kept ahead of column sorting, so these rows stay first. */
    pinnedOrderBy?: EntityOrderBy<EntityType>;
    where?: EntityFilter<EntityType>;
    enabled?: boolean;
    initialLimit?: number;
    onPageReset?: VoidFunction;
    uniqueQueryId: string;
    queryOptions?: Partial<PaginationConfig<EntityType, any[]>>;
    mapRows?: (rows: EntityType[]) => Promise<EntityType[]> | EntityType[];
}

export function useHyperTableFind<EntityType>(
    entity: ClassType<EntityType>,
    {
        enabled = true,
        initialLimit = 20,
        sessionStorageKey,
        uniqueQueryId,
        onPageReset,
        queryOptions,
        ...props
    }: RemultReactTableConfig<EntityType>,
    sorting: SortingState,
    columnFilters: EntityFilter<EntityType>[],
) {
    const debouncedColumnFilters = useDebounce(columnFilters, 300);

    const [pagination, setPagination] = useSessionStorage<PaginationState>(
        sessionStorageKey,
        {
            pageIndex: 0,
            pageSize: initialLimit,
        },
        (p) =>
            typeof p === 'object' &&
            p !== null &&
            'pageIndex' in p &&
            typeof p.pageIndex === 'number' &&
            'pageSize' in p &&
            typeof p.pageSize === 'number',
    );

    const { pageIndex: page, pageSize: limit } = pagination;

    const where: EntityFilter<EntityType> = useMemo(() => {
        return {
            $and: [
                ...columnFilters.filter((v) => !!Object.keys(v).length),
                props?.where!,
            ],
        } as EntityFilter<EntityType>;
    }, [
        JSON.stringify(
            debouncedColumnFilters.filter((v) => !!Object.keys(v).length),
        ),
        JSON.stringify(props?.where),
    ]);

    const orderBy = useMemo(() => {
        const columnOrder: EntityOrderBy<EntityType> =
            sorting.length === 0
                ? (props?.initialOrderBy ?? {})
                : ({
                      [sorting[0].id as keyof EntityOrderBy<EntityType>]:
                          sorting[0].id === userRepo.fields.dateOfBirth.key
                              ? sorting[0].desc
                                  ? 'asc'
                                  : 'desc'
                              : sorting[0].desc
                                ? 'desc'
                                : 'asc',
                  } as EntityOrderBy<EntityType>);
        const pinned = props?.pinnedOrderBy;
        if (!pinned || Object.keys(pinned).length === 0) return columnOrder;
        const merged = { ...pinned, ...columnOrder };
        for (const key of Object.keys(pinned) as Array<keyof typeof pinned>) {
            merged[key] = pinned[key];
        }
        return merged;
    }, [
        sorting,
        JSON.stringify(props?.initialOrderBy),
        JSON.stringify(props?.pinnedOrderBy),
    ]);

    const repo = useMemo(() => remult.repo(entity), [entity]);

    const countFn = useCallback(async () => {
        return await repo.count(where);
    }, [repo, where]);

    const dataFn = useCallback<PaginationMethod<EntityType>>(
        async (pagination) => {
            const rows = await repo.find({
                orderBy,
                where,
                include: props?.include,
                ...(limit === Infinity
                    ? {}
                    : {
                          limit: pagination.limit,
                          page: pagination.page + 1,
                      }),
            });
            return props.mapRows ? props.mapRows(rows) : rows;
        },
        [repo, orderBy, where, JSON.stringify(props?.include), props.mapRows],
    );

    const paginationData = usePagination<EntityType, any[]>({
        name: uniqueQueryId,
        countFn,
        dataFn,
        staleTime: 60 * 1000,
        deps: [
            entity,
            orderBy,
            JSON.stringify(where),
            JSON.stringify(props?.include),
        ],
        pagination: {
            page,
            limit,
        },
        onOutOfRange: () => {
            setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
            }));
        },
        ...queryOptions,
    });

    return {
        ...paginationData,
        pagination,
        setPagination,
        tableProps: {
            data: paginationData.currentPageData ?? [],
            rowCount: paginationData.totalRows ?? undefined,
            pageCount:
                paginationData.isLoading || !paginationData.totalRows
                    ? -1
                    : paginationData.totalRows / limit,
        },
        where: where as Readonly<typeof where>,
    };
}
